import pino from 'pino';
import * as Sentry from '@sentry/react';

// Create browser-compatible logger
const isDevelopment = import.meta.env.DEV;

// ── Error deduplication + rate limit ────────────────────────────────────────
// Protects the Sentry error budget from bugs that fire in a tight loop.
// Two layers:
//   1. Dedup window — same message within 60s is dropped.
//   2. Rate limit — at most 30 Sentry events per minute from this tab.
// Previously the window was 5s only, which still allowed ~12 identical events
// per minute plus unlimited distinct errors — enough to burn the quota fast.
const DEDUP_WINDOW_MS = 60_000;
const RATE_LIMIT_MAX_PER_MINUTE = 30;
const recentErrors = new Map<string, number>();
let rateWindowStart = 0;
let rateWindowCount = 0;

function shouldSkipSentry(msg: string): boolean {
  const now = Date.now();

  if (now - rateWindowStart > 60_000) {
    rateWindowStart = now;
    rateWindowCount = 0;
  }
  if (rateWindowCount >= RATE_LIMIT_MAX_PER_MINUTE) return true;

  const lastSeen = recentErrors.get(msg);
  if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) return true;
  recentErrors.set(msg, now);

  if (recentErrors.size > 100) {
    for (const [key, ts] of recentErrors) {
      if (now - ts > DEDUP_WINDOW_MS) recentErrors.delete(key);
    }
  }

  rateWindowCount += 1;
  return false;
}

// ── Meta handoff to Sentry ──────────────────────────────────────────────────
// pino's browser serializer stringifies the merge object to '[Object]' BEFORE
// transmit runs, so the meta passed to log.error()/log.warn() (which usually
// holds the actual error message) never reached Sentry — every incident read
// as "[Object]". The wrappers below stash a JSON-serialized copy just before
// calling pino; transmit runs synchronously in the same call and picks it up.
let pendingMeta: string | undefined;

const serializeMeta = (meta: unknown): string | undefined => {
  if (meta === undefined || meta === null) return undefined;
  if (meta instanceof Error) return `${meta.name}: ${meta.message}`;
  if (typeof meta === 'string') return meta.slice(0, 2000);
  try {
    return JSON.stringify(
      meta,
      (_key, value) => (value instanceof Error ? `${value.name}: ${value.message}` : value),
    )?.slice(0, 2000);
  } catch {
    return Object.prototype.toString.call(meta);
  }
};

const baseLogger = pino({
  level: isDevelopment ? 'debug' : 'warn',
  browser: {
    asObject: true,
    serialize: true,
    transmit: {
      level: 'warn',
      send: (level, logEvent) => {
        // pino browser transmit: messages = [mergeObject, messageString] or [messageString]
        // With asObject:true + serialize:true, objects may be stringified to '[object Object]'.
        // Strategy: find best string message, skip '[object Object]', extract from objects.
        const messages = logEvent.messages ?? [];
        let msgStr = '';
        let errorObj: Error | undefined;

        // Pass 1: find a real string message and any Error instance
        for (let i = messages.length - 1; i >= 0; i--) {
          const m = messages[i];
          if (m instanceof Error) {
            errorObj = m;
          } else if (typeof m === 'string' && m !== '[object Object]' && m.length > 0) {
            if (!msgStr) msgStr = m;
          }
        }

        // Pass 2: if no string found, extract from object properties
        if (!msgStr) {
          for (const m of messages) {
            if (m && typeof m === 'object' && !(m instanceof Error)) {
              const obj = m as Record<string, unknown>;
              if (typeof obj.msg === 'string' && obj.msg.length > 0) { msgStr = obj.msg; break; }
              if (typeof obj.message === 'string' && obj.message.length > 0) { msgStr = obj.message; break; }
            }
          }
        }

        if (!msgStr) {
          msgStr = errorObj?.message || 'Unknown error';
        }

        // Send errors and fatals to Sentry for production monitoring
        // Skip duplicates + rate-limited events to protect error budget
        if (level === 'error' || level === 'fatal') {
          if (!shouldSkipSentry(msgStr)) {
            Sentry.captureException(
              errorObj ?? new Error(msgStr),
              { extra: { logEvent, level, meta: pendingMeta } }
            );
          }
        }
        // Send warnings as Sentry breadcrumbs for context
        if (level === 'warn') {
          Sentry.addBreadcrumb({
            category: 'logger',
            message: pendingMeta ? `${msgStr} | ${pendingMeta}` : msgStr,
            level: 'warning' });
        }
      }
    }
  },
  formatters: {
    level: (label) => {
      return { level: label };
    }
  }
});

// Helper to sanitize data before logging (remove sensitive info)
const sanitize = (data: unknown): unknown => {
  if (!data || typeof data !== 'object') return data;
  
  const sensitive = ['password', 'token', 'apiKey', 'secret', 'authorization'];
  const sanitized = { ...(data as Record<string, unknown>) };
  
  for (const key of Object.keys(sanitized)) {
    if (sensitive.some(s => key.toLowerCase().includes(s))) {
      sanitized[key] = '[REDACTED]';
    }
  }
  
  return sanitized;
};

// Export convenience methods with sanitization
// Ensure errors sent to pino are real Error instances so the transmit handler
// can extract a useful message instead of logging "[object Object]".
const toLoggable = (val: unknown): Record<string, unknown> | Error => {
  if (val instanceof Error) return val;
  if (val && typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    // If the object wraps an Error (e.g. { error: new Error(...) }), surface it
    if (obj.error instanceof Error) return obj.error;
    return obj;
  }
  if (typeof val === 'string') return { message: val };
  return {};
};

// Export convenience methods with sanitization
export const log = {
  debug: (msg: string, data?: unknown) => baseLogger.debug(sanitize(data), msg),
  info: (msg: string, data?: unknown) => baseLogger.info(sanitize(data), msg),
  warn: (msg: string, data?: unknown) => {
    const cleaned = sanitize(data);
    pendingMeta = serializeMeta(cleaned);
    try {
      baseLogger.warn(cleaned, msg);
    } finally {
      pendingMeta = undefined;
    }
  },
  error: (msg: string, error?: unknown) => {
    const cleaned = sanitize(error);
    pendingMeta = serializeMeta(cleaned);
    try {
      baseLogger.error(toLoggable(cleaned), msg);
    } finally {
      pendingMeta = undefined;
    }
  } };

// Legacy export for backward compatibility
export const logger = log;
